import { HttpErrorResponse, HttpParams, HttpResponse } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BaseService} from '../../shared/base.service';
import { DeploymentProcessModel, BackgroundDeploymentResponse } from '@deployment-common/deployment-process.model';
import { EMPTY, Observable, throwError } from 'rxjs';
import { IssueModel } from '@common/model/issue.model';
import { GenerationOutputModel, PlanningOutputModel } from '../deploy/deployment-preview/planning-output.model';
import { DeploymentStatusModel, GenerationStatusModel } from '@deployment-common/generation-status.model';
import { Status } from '@deployment-common/validating/deployment-status.model';
import { POLL_INTERVAL } from '@common/constants/app.constants';
import { poll } from '../../rx.utils';
import { DeploymentHelper } from './deployment.helper';
import { HTTP_STATUS_CONFLICT } from '../../shared/http-status-codes.constants';

@Injectable()
export class DeploymentService extends BaseService {

  createDeploymentProcess(deployment: DeploymentProcessModel): Observable<DeploymentProcessModel> {
    const url = '/deployments';
    return this.httpPost(url, deployment);
  }

  deleteDeploymentProcess(deploymentId: string): Observable<void> {
    const url = `/deployments/${deploymentId}`;
    return this.httpDelete(url);
  }

  getDeploymentProcess(deploymentId: string): Observable<DeploymentProcessModel> {
    const url = `/deployments/${deploymentId}`;
    return this.httpGet(url);
  }

  startGeneration(deploymentId: string): Observable<GenerationStatusModel> {
    const url = `/deployments/${deploymentId}/generation`;
    // it sends undefined in body since the back end doesn't expect any body
    return this.httpPut(url, undefined);
  }

  abortGeneration(deploymentId: string): Observable<void> {
    const url = `/deployments/${deploymentId}/generation`;
    return this.httpDelete(url).pipe(
      catchError((error: HttpErrorResponse) => {
        // ignore error if trying to abort already completed generation
        if (error.status === HTTP_STATUS_CONFLICT) {
          return EMPTY;
        }
        return throwError(error);
      })
    );
  }

  private getStatusOfGenerationStep(deploymentId: string): Observable<GenerationStatusModel> {
    const url = `/deployments/${deploymentId}/generation`;
    return this.httpGet(url);
  }

  pollGenerationStatus(deploymentId: string): Observable<GenerationStatusModel> {
    return this.pollStatus<GenerationStatusModel>(() => this.getStatusOfGenerationStep(deploymentId));
  }

  getGenerationIssues(deploymentId: string): Observable<IssueModel[]> {
    const url = `/deployments/${deploymentId}/generation/issues`;
    return this.httpGetUnwrapped(url);
  }

  getGenerationOutput(deploymentId: string): Observable<GenerationOutputModel[]> {
    const url = `/deployments/${deploymentId}/generation/output`;
    return this.httpGetUnwrapped(url).pipe(map((generationOutput: GenerationOutputModel[]) => {
      return DeploymentHelper.fixDeploymentResponse(generationOutput);
    }));
  }

  startDeploymentPlanning(deploymentId: string): Observable<DeploymentStatusModel> {
    const url = `/deployments/${deploymentId}/plan`;
    return this.httpPut(url, undefined);
  }

  startForceDeploymentPlanning(deploymentId: string): Observable<DeploymentStatusModel> {
    const url = `/deployments/${deploymentId}/plan`;
    const params: HttpParams = new HttpParams().set('scan', 'false');
    // it sends undefined in body since the back end doesn't expect any body
    return this.httpPut(url, undefined, undefined, params);
  }

  pollDeploymentPlanningStatus(deploymentId: string): Observable<DeploymentStatusModel | undefined> {
    return this.pollStatus<DeploymentStatusModel>(() => this.getStatusOfDeploymentPlanning(deploymentId));
  }

  abortDeploymentPlanning(deploymentId: string): Observable<void> {
    const url = `/deployments/${deploymentId}/plan`;
    return this.httpDelete(url).pipe(
      // ignore error if trying to abort already completed planning
      catchError((error: HttpErrorResponse) => {
        if (error.status === HTTP_STATUS_CONFLICT) {
          return EMPTY;
        }
        return throwError(error);
      })
    );
  }

  getStatusOfDeploymentPlanning(deploymentId: string): Observable<DeploymentStatusModel> {
    const url = `/deployments/${deploymentId}/plan`;
    return this.httpGet(url);
  }

  getPlanningResult(deploymentId: string): Observable<PlanningOutputModel[]> {
    const url = `/deployments/${deploymentId}/plan/output`;
    return this.httpGetUnwrapped(url);
  }

  startDeployment(deploymentId: string, comment: string): Observable<void> {
    const url = `/deployments/${deploymentId}/deploy`;
    return this.httpPut(url, {comment: comment});
  }

  public getStatusOfDeploy(deploymentId: string): Observable<DeploymentStatusModel> {
    const url = `/deployments/${deploymentId}/deploy`;
    return this.httpGet(url);
  }

  pollDeploymentStatus(deploymentId: string): Observable<DeploymentStatusModel> {
    return this.pollStatus<DeploymentStatusModel>(() => this.getStatusOfDeploy(deploymentId));
  }

  /**
   * Polls status job until it's done
   * @param functionToPoll the backend call that the polling will repeat
   */
  private pollStatus<T extends DeploymentStatusModel>(functionToPoll: () => Observable<T>): Observable<T> {
    const pollCondition = (result: T): boolean => result.status !== Status.Done;
    return poll<T>(POLL_INTERVAL, functionToPoll, pollCondition);
  }

  public static createGenerationResultUrl(deploymentId: string): string {
    return `/deployments/${deploymentId}/generation/content`;
  }

  /**
   * Creates a quick deployment with the selected properties and return the job URL.
   */
  public createBackgroundDeployment(request: DeploymentProcessModel): Observable<BackgroundDeploymentResponse> {
    return this.httpPost<DeploymentProcessModel, DeploymentProcessModel, BackgroundDeploymentResponse>(
      '/quick-deployments',
      request,
      (response: HttpResponse<DeploymentProcessModel>): BackgroundDeploymentResponse => {
        const deploymentId: string | undefined = response.body?.deploymentId;
        if (!deploymentId) {
          const msg = 'DeploymentService#createBackgroundDeployment: deploymentId not found in the response body';
          console.error(msg, response.body);
          throw new Error(msg);
        }
        return {
          jobUrl: this.extractLocationHeader(response),
          deploymentId,
        };
      },
    );
  }
}
